import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const GameActionButton = ({
  onClick,
  disabled,
  icon,
  title,
  subtitle,
  accentClass,
  ringClass
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    style={{ WebkitTapHighlightColor: 'transparent' }}
    className={`
      group relative w-full overflow-hidden rounded-2xl border text-left transition-all duration-200
      disabled:cursor-not-allowed disabled:opacity-45
      active:scale-[0.98]
      ${accentClass}
      ${ringClass}
    `}
  >
    <span className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/[0.12] to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
    <span className="relative flex items-center gap-3 px-4 py-3.5 sm:px-5 sm:py-4">
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-black/25 text-2xl shadow-inner ring-1 ring-white/10 transition-transform duration-200 group-hover:scale-105 group-disabled:scale-100">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold tracking-wide text-white drop-shadow-sm">{title}</span>
        {subtitle ? (
          <span className="mt-0.5 block text-xs font-medium text-white/65">{subtitle}</span>
        ) : null}
      </span>
      <span className="text-white/40 transition-transform group-hover:translate-x-0.5 group-disabled:translate-x-0">›</span>
    </span>
  </button>
);

const BlackjackPage = () => {
  const navigate = useNavigate();
  const [isUser, setIsUser] = useState(false);
  const [userCoins, setUserCoins] = useState(0);
  const [settings, setSettings] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [betAmount, setBetAmount] = useState(10);
  const [gameLoading, setGameLoading] = useState(false);
  const [dealerRevealing, setDealerRevealing] = useState(false);
  const [revealedDealerCards, setRevealedDealerCards] = useState([]);
  const [hiddenCardRevealed, setHiddenCardRevealed] = useState(false);
  const [currentDealerValue, setCurrentDealerValue] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);

  useEffect(() => {
    checkUser();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUser) {
      fetchUserCoins();
      checkEmailVerification();
    }
  }, [isUser]);

  const finishGame = useCallback(async () => {
    try {
      setGameLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await axios.post(
        '/api/games/blackjack/action',
        { action: 'finishGame', roundId: gameState.roundId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setGameState(response.data.gameState);
      setUserCoins(response.data.newBalance);
      setDealerRevealing(false);
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      userData.coins = response.data.newBalance;
      localStorage.setItem('userData', JSON.stringify(userData));
      window.dispatchEvent(new Event('userTokenChanged'));

      // Result will be shown on the page, no alert needed
    } catch (error) {
      console.error('Error finishing game:', error);
      // Error will be handled silently, user can see the game state
    } finally {
      setGameLoading(false);
    }
  }, [gameState]);

  const calculateHandValue = (hand) => {
    if (!hand || hand.length === 0) return 0;
    let value = 0;
    let aces = 0;
    
    hand.forEach(card => {
      if (card.rank === 'A') {
        aces++;
        value += 11;
      } else if (['J', 'Q', 'K'].includes(card.rank)) {
        value += 10;
      } else {
        value += parseInt(card.rank) || 0;
      }
    });
    
    while (value > 21 && aces > 0) {
      value -= 10;
      aces--;
    }
    
    return value;
  };

  const revealDealerCards = useCallback(async () => {
    if (!gameState?.awaitingDealerReveal) return;
    
    setDealerRevealing(true);
    setRevealedDealerCards([]);
    setHiddenCardRevealed(false);
    setCurrentDealerValue(null);

    // Önce kapalı kartı aç (ikinci kart) - hızlı
    if (gameState?.dealerHand && gameState.dealerHand.length > 1) {
      await new Promise(resolve => setTimeout(resolve, 600));
      setHiddenCardRevealed(true); // Kapalı kart artık açık
      // Kapalı kart açıldığında değeri hesapla
      const allCards = [...gameState.dealerHand];
      const value = calculateHandValue(allCards);
      setCurrentDealerValue(value);
    }

    // Sonra yeni kartları tek tek çek (eğer varsa) - her kart arasında kısa bekleme
    if (gameState.dealerCardsToDraw && gameState.dealerCardsToDraw.length > 0) {
      let currentCards = [...gameState.dealerHand];
      for (let i = 0; i < gameState.dealerCardsToDraw.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 550)); // kartlar arası kısa bekleme
        currentCards.push(gameState.dealerCardsToDraw[i]);
        setRevealedDealerCards(prev => [...prev, gameState.dealerCardsToDraw[i]]);
        // Her kart çekildiğinde değeri güncelle
        const value = calculateHandValue(currentCards);
        setCurrentDealerValue(value);
      }
    }

    // After all cards are revealed, finish the game - kısa bekleme
    await new Promise(resolve => setTimeout(resolve, 400));
    finishGame();
  }, [gameState?.awaitingDealerReveal, gameState?.dealerCardsToDraw, gameState?.dealerHand, finishGame]);

  useEffect(() => {
    // Handle dealer card reveal animation
    // awaitingDealerReveal true olduğunda kapalı kartı aç ve gerekirse yeni kartları çek
    if (gameState?.awaitingDealerReveal && !dealerRevealing) {
      revealDealerCards();
    }
  }, [gameState?.awaitingDealerReveal, dealerRevealing, revealDealerCards]);

  const checkUser = () => {
    const token = localStorage.getItem('userToken');
    setIsUser(!!token);
    if (!token) {
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    }
  };

  const fetchUserCoins = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get('/api/users/me/coins', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserCoins(response.data.coins || 0);
    } catch (error) {
      console.error('Error fetching user coins:', error);
    }
  };

  const checkEmailVerification = async () => {
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.get('/api/users/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailVerified(response.data.emailVerified || response.data.email_verified || false);
    } catch (error) {
      console.error('Error checking email verification:', error);
    }
  };

  const handleResendVerification = async () => {
    try {
      setResendingEmail(true);
      const token = localStorage.getItem('userToken');
      const response = await axios.post('/api/auth/resend-verification', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(response.data.message || 'Doğrulama e-postası tekrar gönderildi');
    } catch (error) {
      alert(error.response?.data?.error || 'E-posta gönderilirken bir hata oluştu');
    } finally {
      setResendingEmail(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/games/settings/blackjack');
      setSettings(response.data);
      if (response.data.minBet || response.data.min_bet) {
        setBetAmount(response.data.minBet || response.data.min_bet);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleBetChange = (value) => {
    if (!settings) return;
    const min = settings.minBet || settings.min_bet || 10;
    const max = settings.maxBet || settings.max_bet || 500;
    let newBet = parseInt(value) || min;
    if (newBet < min) newBet = min;
    if (newBet > max) newBet = max;
    if (newBet > userCoins) newBet = userCoins;
    setBetAmount(newBet);
  };

  const handleQuickBet = (multiplier) => {
    if (!settings) return;
    const min = settings.minBet || settings.min_bet || 10;
    const max = settings.maxBet || settings.max_bet || 500;
    let newBet = Math.floor(betAmount * multiplier);
    if (newBet < min) newBet = min;
    if (newBet > max) newBet = max;
    if (newBet > userCoins) newBet = userCoins;
    setBetAmount(newBet);
  };

  const startGame = async () => {
    if (!isUser) {
      alert('Oyun oynamak için giriş yapmalısınız');
      navigate('/login');
      return;
    }

    if (!emailVerified) {
      alert('Oyun oynamak için e-posta adresinizi doğrulamanız gerekmektedir. E-postanızı kontrol edin veya doğrulama e-postasını tekrar gönderin.');
      return;
    }

    if (betAmount > userCoins) {
      alert('Yetersiz puan!');
      return;
    }

    if (!settings) {
      alert('Oyun ayarları yüklenemedi');
      return;
    }

    const min = settings.minBet || settings.min_bet || 10;
    const max = settings.maxBet || settings.max_bet || 500;

    if (betAmount < min || betAmount > max) {
      alert(`Bahis miktarı ${min} ile ${max} arasında olmalıdır`);
      return;
    }

    try {
      setGameLoading(true);
      setRevealedDealerCards([]);
      setHiddenCardRevealed(false);
      setCurrentDealerValue(null);
      const token = localStorage.getItem('userToken');
      const response = await axios.post(
        '/api/games/blackjack/start',
        { betAmount },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setGameState(response.data.gameState);
      setUserCoins(response.data.newBalance);
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      userData.coins = response.data.newBalance;
      localStorage.setItem('userData', JSON.stringify(userData));
      window.dispatchEvent(new Event('userTokenChanged'));
    } catch (error) {
      console.error('Error starting game:', error);
      // Error will be handled silently
    } finally {
      setGameLoading(false);
    }
  };


  const handleAction = async (action) => {
    if (!gameState || gameState.gameOver || dealerRevealing) return;

    try {
      setGameLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await axios.post(
        '/api/games/blackjack/action',
        { action, roundId: gameState.roundId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setGameState(response.data.gameState);
      setUserCoins(response.data.newBalance);
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      userData.coins = response.data.newBalance;
      localStorage.setItem('userData', JSON.stringify(userData));
      window.dispatchEvent(new Event('userTokenChanged'));

      // Result will be shown on the page, no alert needed
    } catch (error) {
      console.error('Error performing action:', error);
      // Error will be handled silently, user can see the game state
    } finally {
      setGameLoading(false);
    }
  };

  const getCardDisplay = (card, index = 0, options = {}) => {
    const isRevealing = typeof options === 'boolean' ? options : options.reveal;
    const animateDeal = typeof options === 'boolean' ? !isRevealing : options.animateDeal !== false;
    const suitSymbols = {
      hearts: '♥',
      diamonds: '♦',
      clubs: '♣',
      spades: '♠'
    };
    const suitColors = {
      hearts: 'text-red-600',
      diamonds: 'text-red-600',
      clubs: 'text-slate-800',
      spades: 'text-slate-800'
    };
    const isRed = card.suit === 'hearts' || card.suit === 'diamonds';
    return (
      <div
        className={`
          mobile-card relative w-20 h-28 sm:w-28 sm:h-40 flex flex-col items-center justify-center
          rounded-xl sm:rounded-2xl border-2 p-2 sm:p-3
          bg-gradient-to-b from-white via-white to-stone-100
          ${isRed ? 'border-red-400/90' : 'border-slate-500/80'}
          shadow-[0_4px_6px_-1px_rgba(0,0,0,0.35),0_12px_24px_-6px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.9)]
          transition-transform duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.35)]
          ${animateDeal && !isRevealing ? 'animate-card-deal' : ''}
          ${isRevealing ? 'animate-card-reveal' : ''}
        `}
        style={{ animationDelay: `${index * 0.08}s` }}
      >
        <div className="pointer-events-none absolute inset-x-2 top-2 h-px bg-gradient-to-r from-transparent via-black/8 to-transparent" />
        <div className={`relative font-bold tabular-nums tracking-tight text-base sm:text-xl lg:text-2xl mb-0.5 sm:mb-1 mobile-card-rank ${suitColors[card.suit]}`}>
          {card.rank}
        </div>
        <div className={`relative text-3xl sm:text-4xl lg:text-5xl leading-none mobile-card-suit ${suitColors[card.suit]}`}>
          {suitSymbols[card.suit]}
        </div>
      </div>
    );
  };

  const getCardBack = (extraClass = '') => {
    return (
      <div
        className={`
          mobile-card relative w-20 h-28 sm:w-28 sm:h-40 overflow-hidden rounded-xl sm:rounded-2xl
          border-2 border-navy-400/80 shadow-[0_8px_24px_-4px_rgba(0,0,0,0.5)]
          bg-gradient-to-br from-navy-700 via-navy-900 to-navy-950
          ${extraClass}
        `}
      >
        <div className="pointer-events-none absolute inset-0 card-back-pattern opacity-30" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        {settings?.cardBackUrl || settings?.card_back_url ? (
          <>
            <img
              src={settings.cardBackUrl || settings.card_back_url}
              alt=""
              className="relative z-[1] h-full w-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.parentElement.querySelector('.card-back-fallback');
                if (fallback) fallback.classList.remove('hidden');
              }}
            />
            <div className="card-back-fallback relative z-[1] hidden h-full w-full items-center justify-center">
              <div className="text-3xl sm:text-4xl text-white/90 drop-shadow-md">♠</div>
            </div>
          </>
        ) : (
          <div className="relative z-[1] flex h-full w-full items-center justify-center">
            <div className="text-3xl sm:text-4xl text-white/90 drop-shadow-md">♠</div>
          </div>
        )}
      </div>
    );
  };

  if (!isUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Oyun oynamak için giriş yapmalısınız</p>
          <p className="text-white/60">Yönlendiriliyorsunuz...</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  const minBet = settings.minBet || settings.min_bet || 10;
  const maxBet = settings.maxBet || settings.max_bet || 500;

  const splitHand1Value = gameState?.isSplit
    ? calculateHandValue(gameState.playerHand1 || [])
    : null;
  const splitHand2Value = gameState?.isSplit
    ? calculateHandValue(gameState.playerHand2 || [])
    : null;

  // Get dealer cards to display
  // Oyun başında: İlk kart açık, ikinci kart kapalı
  // awaitingDealerReveal: Kapalı kart açılır, sonra yeni kartlar çekilir
  // gameOver: Tüm kartlar açık
  const dealerCardsToShow = gameState?.gameOver
    ? (gameState?.dealerFinalHand || gameState?.dealerHand || []) // Oyun bittiğinde tüm kartlar açık
    : gameState?.awaitingDealerReveal && hiddenCardRevealed
    ? [
        ...(gameState?.dealerHand || []).slice(0, 2), // İlk iki kart (kapalı kart artık açık)
        ...revealedDealerCards // Yeni çekilen kartlar
      ]
    : gameState?.awaitingDealerReveal && !hiddenCardRevealed
    ? (gameState?.dealerHand || []).slice(0, 1) // Kapalı kart henüz açılmadı, sadece ilk kart göster
    : (gameState?.dealerHand || []).slice(0, 1); // Oyun başında sadece ilk kart açık
  
  // Kapalı kart gösterilmeli mi?
  // Oyun başında ve awaitingDealerReveal false ise kapalı kartı göster
  const shouldShowHiddenCard = gameState && !gameState.gameOver && !gameState.awaitingDealerReveal && gameState.dealerHasHiddenCard;

  return (
    <div className="min-h-screen bg-gradient-dark py-4 sm:py-8 px-2 sm:px-4 overflow-x-hidden">
      <style>{`
        .card-back-pattern {
          background-image:
            repeating-linear-gradient(
              -28deg,
              transparent,
              transparent 6px,
              rgba(255,255,255,0.04) 6px,
              rgba(255,255,255,0.04) 7px
            ),
            repeating-linear-gradient(
              28deg,
              transparent,
              transparent 6px,
              rgba(0,0,0,0.06) 6px,
              rgba(0,0,0,0.06) 7px
            );
        }
        @keyframes card-reveal {
          0% {
            transform: perspective(600px) rotateY(-92deg) scale(0.92);
            opacity: 0;
            filter: brightness(1.15);
          }
          55% {
            transform: perspective(600px) rotateY(8deg) scale(1.02);
            opacity: 1;
          }
          100% {
            transform: perspective(600px) rotateY(0deg) scale(1);
            opacity: 1;
            filter: brightness(1);
          }
        }
        .animate-card-reveal {
          animation: card-reveal 0.65s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes card-deal {
          0% {
            transform: translate(42px, -72px) rotate(18deg) scale(0.88);
            opacity: 0;
            filter: blur(2px);
          }
          70% {
            transform: translate(-4px, 4px) rotate(-2deg) scale(1.02);
            opacity: 1;
            filter: blur(0);
          }
          100% {
            transform: translate(0, 0) rotate(0deg) scale(1);
            opacity: 1;
            filter: blur(0);
          }
        }
        .animate-card-deal {
          animation: card-deal 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes dealer-wait-shimmer {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.85; }
        }
        .dealer-pending-back {
          animation: dealer-wait-shimmer 1.4s ease-in-out infinite;
        }
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 0 16px rgba(212, 175, 55, 0.35), inset 0 0 0 1px rgba(212, 175, 55, 0.2);
          }
          50% {
            box-shadow: 0 0 32px rgba(212, 175, 55, 0.55), inset 0 0 0 1px rgba(212, 175, 55, 0.35);
          }
        }
        .pulse-glow {
          animation: pulse-glow 2.8s ease-in-out infinite;
        }
        @keyframes bj-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        .bj-loading-bar {
          animation: bj-bar 0.9s ease-in-out infinite;
        }
        @media (max-width: 640px) {
          .mobile-card {
            width: 72px !important;
            height: 102px !important;
            padding: 8px !important;
          }
          .mobile-card-rank {
            font-size: 15px !important;
          }
          .mobile-card-suit {
            font-size: 34px !important;
          }
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto w-full overflow-x-hidden">
        {/* Header */}
        <div className="mb-4 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => navigate('/games')}
            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 shadow-sm backdrop-blur-md transition hover:border-gold/40 hover:bg-white/10 hover:text-gold"
          >
            <span className="text-lg leading-none">‹</span>
            <span>Saguex Originals</span>
          </button>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
            <div className="pulse-glow w-full rounded-2xl border border-gold/35 bg-gradient-to-br from-gold/15 to-amber-900/20 px-5 py-3 shadow-lg backdrop-blur-sm sm:w-auto">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gold/80">Bakiye</div>
              <div className="text-center font-bold tabular-nums text-gold sm:text-left text-lg sm:text-xl">
                {userCoins.toLocaleString('tr-TR')} <span className="text-sm font-semibold text-gold/70">puan</span>
              </div>
            </div>
            <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-2 text-center text-xs text-white/70 backdrop-blur-sm sm:text-left">
              <span className="text-white/50">Limit </span>
              <span className="font-medium text-white/85">{minBet}</span>
              <span className="text-white/40"> — </span>
              <span className="font-medium text-white/85">{maxBet}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-8">
          {/* Left Control Panel */}
          <div className="order-2 lg:order-1 lg:col-span-1">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-navy-800/90 to-navy-900/95 p-4 shadow-2xl shadow-black/40 backdrop-blur-md sm:rounded-3xl sm:p-6 ring-1 ring-gold/20">
              {gameLoading && (
                <div className="mb-3 h-1 w-full overflow-hidden rounded-full bg-white/10">
                  <div className="bj-loading-bar h-full w-1/3 rounded-full bg-gold/80" />
                </div>
              )}
              {!gameState ? (
                <>
                  <div className="mb-5 text-center">
                    <h3 className="text-lg font-bold tracking-tight text-gold">Bahis</h3>
                    <p className="mt-1 text-xs text-white/55">Masaya oturmadan önce miktarı seçin</p>
                  </div>

                  <div className="mb-5">
                    <label className="mb-2 block text-center text-[11px] font-semibold uppercase tracking-widest text-white/45">
                      Tutar (puan)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={betAmount}
                        onChange={(e) => handleBetChange(e.target.value)}
                        className="min-w-0 flex-1 rounded-xl border border-white/10 bg-black/35 px-4 py-3 text-center text-lg font-semibold tabular-nums text-white shadow-inner outline-none ring-gold/30 transition focus:border-gold/50 focus:ring-2"
                        min={minBet}
                        max={Math.min(maxBet, userCoins)}
                      />
                      <div className="flex shrink-0 flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => handleQuickBet(0.5)}
                          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10"
                        >
                          ÷2
                        </button>
                        <button
                          type="button"
                          onClick={() => handleQuickBet(2)}
                          className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/80 transition hover:bg-white/10"
                        >
                          ×2
                        </button>
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setBetAmount(minBet)}
                        className="flex-1 rounded-xl border border-gold/25 bg-gold/10 py-2.5 text-xs font-semibold text-gold transition hover:bg-gold/20"
                      >
                        Min
                      </button>
                      <button
                        type="button"
                        onClick={() => setBetAmount(Math.min(maxBet, userCoins))}
                        className="flex-1 rounded-xl border border-gold/25 bg-gold/10 py-2.5 text-xs font-semibold text-gold transition hover:bg-gold/20"
                      >
                        Max
                      </button>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={startGame}
                    disabled={gameLoading || betAmount > userCoins || betAmount < minBet || betAmount > maxBet}
                    className="w-full rounded-2xl bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-600 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-emerald-900/40 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {gameLoading ? 'Dağıtılıyor…' : 'Bahis yap ve başla'}
                  </button>

                  {betAmount > userCoins && (
                    <p className="mt-3 text-center text-sm text-red-400/90">Yetersiz puan</p>
                  )}
                </>
              ) : (
                <>
                  <div className="mb-5 rounded-2xl border border-white/10 bg-black/25 p-4">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-white/45">Bu el</div>
                    <div className="mt-1 flex items-baseline justify-between gap-2">
                      <span className="text-2xl font-bold tabular-nums text-gold">{gameState.betAmount}</span>
                      <span className="text-sm text-white/50">puan</span>
                    </div>
                  </div>

                  {!gameState.gameOver && !dealerRevealing && !gameState.allHandsDone && (
                    <div className="relative z-10 space-y-2.5">
                      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/40">
                        Hamleler
                      </p>
                      {gameState.isSplit && (
                        <div className="mb-3 rounded-2xl border border-gold/30 bg-black/30 p-3">
                          <div className="text-center text-[10px] font-semibold uppercase tracking-widest text-white/45">
                            Aktif el
                          </div>
                          <div className="mt-2 flex items-center justify-center gap-2">
                            <span
                              className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                                gameState.currentHand === 1
                                  ? 'bg-gold text-navy-900 shadow-md shadow-gold/30'
                                  : 'bg-white/5 text-white/70'
                              }`}
                            >
                              Sol
                            </span>
                            <span className="text-white/25">|</span>
                            <span
                              className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${
                                gameState.currentHand === 2
                                  ? 'bg-gold text-navy-900 shadow-md shadow-gold/30'
                                  : 'bg-white/5 text-white/70'
                              }`}
                            >
                              Sağ
                            </span>
                          </div>
                        </div>
                      )}
                      <GameActionButton
                        onClick={() => handleAction('hit')}
                        disabled={
                          gameLoading ||
                          (gameState.isSplit
                            ? gameState.currentHand === 1
                              ? splitHand1Value >= 21
                              : splitHand2Value >= 21
                            : gameState.playerValue >= 21)
                        }
                        icon={<span className="text-xl">＋</span>}
                        title="Kart iste"
                        subtitle="Hit — elinize bir kart daha"
                        accentClass="border-navy-500/40 bg-gradient-to-br from-navy-600/90 to-navy-800/90 shadow-lg shadow-navy-900/30"
                        ringClass="hover:ring-2 hover:ring-navy-400/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-400/50"
                      />
                      <GameActionButton
                        onClick={() => handleAction('stand')}
                        disabled={gameLoading}
                        icon={<span className="text-lg">✓</span>}
                        title="Kal"
                        subtitle="Stand — sıra krupiyede"
                        accentClass="border-navy-500/40 bg-gradient-to-br from-navy-600/90 to-navy-900/90 shadow-lg shadow-navy-900/30"
                        ringClass="hover:ring-2 hover:ring-violet-400/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50"
                      />
                      {gameState.canDouble && (
                        <GameActionButton
                          onClick={() => handleAction('double')}
                          disabled={
                            gameLoading ||
                            (gameState.isSplit
                              ? gameState.currentHand === 1
                                ? userCoins < gameState.betAmount
                                : userCoins < gameState.betAmount2
                              : userCoins < gameState.betAmount)
                          }
                          icon={<span className="text-lg font-black">×2</span>}
                          title="Katla"
                          subtitle="Double — bahis ikiye katlanır, tek kart"
                          accentClass="border-emerald-500/40 bg-gradient-to-br from-emerald-600/90 to-teal-900/90 shadow-lg shadow-emerald-900/30"
                          ringClass="hover:ring-2 hover:ring-emerald-400/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
                        />
                      )}
                      {gameState.canSplit &&
                        gameState.playerHand &&
                        gameState.playerHand.length === 2 &&
                        !gameState.isSplit && (
                          <GameActionButton
                            onClick={() => handleAction('split')}
                            disabled={gameLoading || userCoins < gameState.betAmount}
                            icon={<span className="text-xl">⎘</span>}
                            title="Böl"
                            subtitle="Split — aynı değerdeki çifti ikiye ayır"
                            accentClass="border-amber-500/40 bg-gradient-to-br from-amber-600/90 to-orange-900/90 shadow-lg shadow-amber-900/25"
                            ringClass="hover:ring-2 hover:ring-amber-400/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/50"
                          />
                        )}
                    </div>
                  )}

                  {dealerRevealing && (
                    <div className="rounded-2xl border border-gold/25 bg-black/30 py-6 text-center">
                      <div className="mx-auto mb-3 h-9 w-9 animate-spin rounded-full border-2 border-gold/30 border-t-gold" />
                      <p className="text-sm font-medium text-white/85">Krupiye oynuyor</p>
                      <p className="mt-1 text-xs text-white/50">Kartlar sırayla açılıyor</p>
                    </div>
                  )}

                  {gameState.gameOver && !dealerRevealing && (
                    <button
                      type="button"
                      onClick={() => {
                        setGameState(null);
                        setRevealedDealerCards([]);
                        setHiddenCardRevealed(false);
                        setCurrentDealerValue(null);
                        fetchUserCoins();
                      }}
                      className="mt-2 w-full rounded-2xl border border-gold/40 bg-gradient-to-r from-amber-400 via-gold to-amber-500 py-3.5 text-sm font-bold uppercase tracking-wide text-navy-900 shadow-lg shadow-amber-900/30 transition hover:brightness-105"
                    >
                      Yeni oyun
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

        {/* Email Verification Warning */}
        {isUser && !emailVerified && (
          <div className="bg-yellow-500/20 border-2 border-yellow-500/50 rounded-xl p-4 mb-6">
            <p className="text-yellow-400 font-semibold mb-2">⚠️ E-posta Doğrulama Gerekli</p>
            <p className="text-white/80 text-sm mb-3">
              Oyun oynamak için e-posta adresinizi doğrulamanız gerekmektedir. E-postanızı kontrol edin veya doğrulama e-postasını tekrar gönderin.
            </p>
            <button
              onClick={handleResendVerification}
              disabled={resendingEmail}
              className="bg-yellow-500 hover:bg-yellow-600 text-navy-900 font-bold px-4 py-2 rounded-lg transition-all disabled:opacity-50"
            >
              {resendingEmail ? 'Gönderiliyor...' : 'Doğrulama E-postası Gönder'}
            </button>
          </div>
        )}

        {/* Game Area — masa */}
          <div className="order-1 lg:order-2 lg:col-span-3">
            <div
              className="relative flex min-h-[520px] flex-col overflow-hidden rounded-3xl border-2 border-gold/45 p-4 shadow-2xl shadow-black/50 sm:min-h-[620px] sm:p-6 lg:min-h-[720px] lg:p-8"
              style={{
                backgroundColor: '#0d3b2c',
                backgroundImage: `
                  radial-gradient(ellipse 120% 80% at 50% -20%, rgba(212, 175, 55, 0.12), transparent 55%),
                  radial-gradient(ellipse 90% 60% at 80% 100%, rgba(0, 0, 0, 0.35), transparent 50%),
                  radial-gradient(ellipse 70% 50% at 15% 40%, rgba(255, 255, 255, 0.04), transparent 45%),
                  repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0, 0, 0, 0.045) 3px, rgba(0, 0, 0, 0.045) 6px)
                `
              }}
            >
              <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-white/5" />
              <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
              <div className="pointer-events-none absolute inset-x-8 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/35 to-transparent" />
                  {!gameState ? (
                <div className="flex-1 flex flex-col items-center justify-center py-8 sm:py-0">
                  {(() => {
                    const logoUrl = settings?.logoUrl || settings?.logo_url || '/images/originals/blackjack.png';
                    return (
                      <div className="mb-4 sm:mb-6 flex justify-center">
                        <img
                          src={logoUrl}
                          alt="Blackjack"
                          className="h-40 sm:h-48 lg:h-56 w-auto rounded-xl object-contain shadow-[0_0_40px_rgba(212,175,55,0.25)]"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling?.classList.remove('hidden');
                          }}
                        />
                        <div className="text-6xl sm:text-7xl lg:text-8xl mb-4 sm:mb-6 hidden">🃏</div>
                      </div>
                    );
                  })()}
                  <p className="mb-6 max-w-md px-4 text-center text-sm leading-relaxed text-white/70 sm:mb-8 sm:text-base">
                    Soldan bahsinizi seçin, ardından masaya katılın. İyi şanslar.
                  </p>
                </div>
              ) : (
                <>
                  {/* Krupiye */}
                  <div className="relative z-10 mb-6 sm:mb-8 lg:mb-10">
                    <div className="mb-4 flex flex-col items-center sm:mb-5">
                      <span className="inline-flex items-center gap-2 rounded-full border border-gold/35 bg-black/35 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold/90 shadow-sm backdrop-blur-sm">
                        Krupiye
                      </span>
                      <div className="mt-3 min-h-[2rem] text-center">
                        {gameState.gameOver ? (
                          <span
                            className={`text-xl font-bold tabular-nums sm:text-2xl ${
                              gameState.dealerValue > 21 ? 'text-red-400' : 'text-gold'
                            }`}
                          >
                            {gameState.dealerValue > 21 ? 'Bust' : `Toplam ${gameState.dealerValue}`}
                          </span>
                        ) : gameState.awaitingDealerReveal ? (
                          <span
                            className={`text-xl font-bold tabular-nums sm:text-2xl transition-colors ${
                              currentDealerValue !== null && currentDealerValue > 21 ? 'text-red-400' : 'text-gold'
                            }`}
                          >
                            {currentDealerValue !== null
                              ? currentDealerValue > 21
                                ? 'Bust'
                                : `Toplam ${currentDealerValue}`
                              : 'Kapalı kart açılıyor…'}
                          </span>
                        ) : (
                          <span className="text-xl font-bold tabular-nums text-gold/85 sm:text-2xl">
                            Görünen {gameState.dealerValue ?? '—'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-end justify-center gap-3 sm:gap-4 lg:gap-5">
                      {dealerCardsToShow.map((card, index) => {
                        const holeJustRevealed =
                          gameState.awaitingDealerReveal &&
                          hiddenCardRevealed &&
                          index === 1 &&
                          (gameState.dealerHand || []).length > 1;
                        const displayOpts = holeJustRevealed
                          ? { reveal: true, animateDeal: false }
                          : { animateDeal: true };
                        return (
                          <div
                            key={`dealer-${card.suit}-${card.rank}-${index}`}
                            className="origin-bottom transition-transform duration-300 hover:z-10 hover:-translate-y-1"
                          >
                            {getCardDisplay(card, index, displayOpts)}
                          </div>
                        );
                      })}
                      {shouldShowHiddenCard && (
                        <div className="origin-bottom animate-card-deal transition-transform duration-300 hover:-translate-y-1">
                          {getCardBack('dealer-pending-back')}
                        </div>
                      )}
                      {gameState?.awaitingDealerReveal &&
                        gameState?.dealerCardsToDraw &&
                        dealerCardsToShow.length <
                          (gameState?.dealerHand?.length || 0) +
                            (gameState?.dealerCardsToDraw?.length || 0) && (
                          <div className="origin-bottom transition-transform duration-300 hover:-translate-y-1">
                            {getCardBack('dealer-pending-back')}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Player Hand(s) */}
                  <div className="mt-auto relative z-10">
                    {gameState.isSplit ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        {/* Sol El (Hand 1) */}
                        <div
                          className={`relative rounded-2xl border p-5 transition-all duration-300 sm:rounded-3xl sm:p-6 ${
                            gameState.currentHand === 1 && !gameState.allHandsDone
                              ? 'border-gold/60 bg-gradient-to-br from-gold/15 via-black/25 to-black/20 shadow-[0_0_0_1px_rgba(212,175,55,0.25),0_12px_40px_-12px_rgba(0,0,0,0.6)] ring-2 ring-gold/30'
                              : 'border-white/10 bg-black/25'
                          }`}
                        >
                          {gameState.currentHand === 1 && !gameState.allHandsDone && (
                            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-gold/[0.06] to-transparent sm:rounded-3xl" />
                          )}
                          <div className="relative mb-4 text-center text-white/90 sm:mb-5">
                            <div className="mb-2 flex flex-wrap items-center justify-center gap-2">
                              <span className="text-sm font-semibold uppercase tracking-wider text-white/80 sm:text-base">
                                Sol el
                              </span>
                              {gameState.currentHand === 1 && !gameState.allHandsDone && (
                                <span className="rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy-900 shadow-sm">
                                  Sıra sizde
                                </span>
                              )}
                            </div>
                            <div
                              className={`mt-2 text-base font-bold tabular-nums sm:text-lg ${
                                splitHand1Value > 21
                                  ? 'text-red-400'
                                  : gameState.playerHand1 &&
                                      gameState.playerHand1.length === 2 &&
                                      splitHand1Value === 21
                                    ? 'text-gold'
                                    : 'text-gold'
                              }`}
                            >
                              Toplam {splitHand1Value}
                              {splitHand1Value > 21 && ' · Bust'}
                              {gameState.playerHand1 &&
                                gameState.playerHand1.length === 2 &&
                                splitHand1Value === 21 &&
                                ' · Blackjack'}
                            </div>
                            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/35 px-3 py-1 text-xs font-medium text-white/75">
                              Bahis{' '}
                              <span className="tabular-nums text-gold">{gameState.betAmount}</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                            {gameState.playerHand1 &&
                              gameState.playerHand1.map((card, index) => (
                                <div
                                  key={`h1-${card.suit}-${card.rank}-${index}`}
                                  className="origin-bottom transition-transform duration-300 hover:z-10 hover:-translate-y-1"
                                >
                                  {getCardDisplay(card, index)}
                                </div>
                              ))}
                          </div>
                        </div>
                        
                        {/* Sağ El (Hand 2) */}
                        <div
                          className={`relative rounded-2xl border p-5 transition-all duration-300 sm:rounded-3xl sm:p-6 ${
                            gameState.currentHand === 2 && !gameState.allHandsDone
                              ? 'border-gold/60 bg-gradient-to-br from-gold/15 via-black/25 to-black/20 shadow-[0_0_0_1px_rgba(212,175,55,0.25),0_12px_40px_-12px_rgba(0,0,0,0.6)] ring-2 ring-gold/30'
                              : 'border-white/10 bg-black/25'
                          }`}
                        >
                          {gameState.currentHand === 2 && !gameState.allHandsDone && (
                            <div className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-transparent via-gold/[0.06] to-transparent sm:rounded-3xl" />
                          )}
                          <div className="relative mb-4 text-center text-white/90 sm:mb-5">
                            <div className="mb-2 flex flex-wrap items-center justify-center gap-2">
                              <span className="text-sm font-semibold uppercase tracking-wider text-white/80 sm:text-base">
                                Sağ el
                              </span>
                              {gameState.currentHand === 2 && !gameState.allHandsDone && (
                                <span className="rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-navy-900 shadow-sm">
                                  Sıra sizde
                                </span>
                              )}
                            </div>
                            <div
                              className={`mt-2 text-base font-bold tabular-nums sm:text-lg ${
                                splitHand2Value > 21
                                  ? 'text-red-400'
                                  : gameState.playerHand2 &&
                                      gameState.playerHand2.length === 2 &&
                                      splitHand2Value === 21
                                    ? 'text-gold'
                                    : 'text-gold'
                              }`}
                            >
                              Toplam {splitHand2Value}
                              {splitHand2Value > 21 && ' · Bust'}
                              {gameState.playerHand2 &&
                                gameState.playerHand2.length === 2 &&
                                splitHand2Value === 21 &&
                                ' · Blackjack'}
                            </div>
                            <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/35 px-3 py-1 text-xs font-medium text-white/75">
                              Bahis{' '}
                              <span className="tabular-nums text-gold">{gameState.betAmount2}</span>
                            </div>
                          </div>
                          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
                            {gameState.playerHand2 &&
                              gameState.playerHand2.map((card, index) => (
                                <div
                                  key={`h2-${card.suit}-${card.rank}-${index}`}
                                  className="origin-bottom transition-transform duration-300 hover:z-10 hover:-translate-y-1"
                                >
                                  {getCardDisplay(card, index)}
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="relative">
                        <div className="mb-4 flex flex-col items-center sm:mb-6">
                          <span className="inline-flex items-center gap-2 rounded-full border border-gold/35 bg-black/35 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold/90 shadow-sm backdrop-blur-sm">
                            Sizin eliniz
                          </span>
                          <div
                            className={`mt-3 text-center text-xl font-bold tabular-nums sm:text-2xl ${
                              gameState.playerValue > 21
                                ? 'text-red-400'
                                : gameState.playerHand &&
                                    gameState.playerHand.length === 2 &&
                                    gameState.playerValue === 21
                                  ? 'text-gold'
                                  : 'text-gold'
                            }`}
                          >
                            Toplam {gameState.playerValue}
                            {gameState.playerValue > 21 && ' · Bust'}
                            {gameState.playerHand &&
                              gameState.playerHand.length === 2 &&
                              gameState.playerValue === 21 &&
                              ' · Blackjack'}
                          </div>
                          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/30 px-3 py-1 text-xs font-medium text-white/75">
                            Bahis <span className="tabular-nums text-gold">{gameState.betAmount}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap justify-center gap-3 sm:gap-4 lg:gap-5">
                          {gameState.playerHand &&
                            gameState.playerHand.map((card, index) => (
                              <div
                                key={`p-${card.suit}-${card.rank}-${index}`}
                                className="origin-bottom transition-transform duration-300 hover:z-10 hover:-translate-y-1"
                              >
                                {getCardDisplay(card, index)}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Result Message */}
                  {gameState.gameOver && !dealerRevealing && (
                    <div className="mt-8 text-center">
                      {gameState.isSplit && gameState.splitResults ? (
                        <div className="space-y-4">
                          <div className={`inline-block px-8 py-4 rounded-xl transition-all duration-500 ${
                            gameState.result === 'win'
                              ? 'bg-green-500/20 border-3 border-green-500'
                              : gameState.result === 'push'
                              ? 'bg-yellow-500/20 border-3 border-yellow-500'
                              : 'bg-red-500/20 border-3 border-red-500'
                          }`}>
                            <div className={`text-xl font-bold mb-3 ${
                              gameState.result === 'win'
                                ? 'text-green-400'
                                : gameState.result === 'push'
                                ? 'text-yellow-400'
                                : 'text-red-400'
                            }`}>
                              {gameState.result === 'win' && 'Toplam Kazanç!'}
                              {gameState.result === 'push' && 'Toplam Berabere!'}
                              {gameState.result === 'lose' && 'Toplam Kayıp'}
                            </div>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              {gameState.splitResults.map((result, idx) => (
                                <div key={idx} className={`p-3 rounded-lg ${
                                  result.result === 'win'
                                    ? 'bg-green-500/20 border border-green-500'
                                    : result.result === 'push'
                                    ? 'bg-yellow-500/20 border border-yellow-500'
                                    : 'bg-red-500/20 border border-red-500'
                                }`}>
                                  <div className="font-semibold mb-1">
                                    {result.hand === 1 ? 'Sol El' : 'Sağ El'}
                                  </div>
                                  <div className={`${
                                    result.result === 'win'
                                      ? 'text-green-400'
                                      : result.result === 'push'
                                      ? 'text-yellow-400'
                                      : 'text-red-400'
                                  }`}>
                                    {result.result === 'win' && `+${result.winAmount}`}
                                    {result.result === 'push' && `${result.winAmount} (İade)`}
                                    {result.result === 'lose' && 'Kayıp'}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="text-gold font-semibold mt-4 text-lg">
                              Toplam: {gameState.winAmount > 0 ? '+' : ''}{gameState.winAmount} Puan
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className={`inline-block px-8 py-4 rounded-xl transition-all duration-500 ${
                          gameState.result === 'win' || gameState.result === 'blackjack'
                            ? 'bg-green-500/20 border-3 border-green-500'
                            : gameState.result === 'push'
                            ? 'bg-yellow-500/20 border-3 border-yellow-500'
                            : 'bg-red-500/20 border-3 border-red-500'
                        }`}>
                          <div className={`text-2xl font-bold ${
                            gameState.result === 'win' || gameState.result === 'blackjack'
                              ? 'text-green-400'
                              : gameState.result === 'push'
                              ? 'text-yellow-400'
                              : 'text-red-400'
                          }`}>
                            {gameState.result === 'win' && 'Kazandınız!'}
                            {gameState.result === 'blackjack' && 'Blackjack! Kazandınız!'}
                            {gameState.result === 'push' && 'Berabere!'}
                            {gameState.result === 'lose' && 'Kaybettiniz'}
                          </div>
                          {gameState.winAmount > 0 ? (
                            <div className="text-gold font-semibold mt-2 text-lg">
                              +{gameState.winAmount} Puan Kazandınız
                            </div>
                          ) : gameState.result === 'lose' ? (
                            <div className="text-red-400 font-semibold mt-2 text-lg">
                              -{gameState.betAmount} Puan Kaybettiniz
                            </div>
                          ) : gameState.result === 'push' ? (
                            <div className="text-yellow-400 font-semibold mt-2 text-lg">
                              {gameState.betAmount} Puan İade Edildi
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlackjackPage;
